'use client';

import type { Profile } from '@channel.io/channel-web-sdk-loader';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ENV, WELLO_IMAGE_PREFIX_URL } from '@/constants';
import { DONATION_ROUTES, ROUTES, WEL_MARKET_ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/AuthStore';
import imageLoader from '@/utils/imageLoader';

export const ChannelTalk = () => {
  const pathname = usePathname();
  const [isLogin, myInfo] = useAuthStore(
    useShallow((state) => [state.isLogin, state.myInfo] as const),
  );

  const [unreadCount, setUnreadCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [isMessengerVisible, setIsMessengerVisible] = useState(false);
  const isMessengerVisibleRef = useRef(false);
  const isChannelLoadedRef = useRef(false);

  // pathname이 정확히 일치할 때만 숨겨야 하는 경로들
  const hiddenExactPathnames = [ROUTES.HOME.pathname];

  // pathname prefix 기준으로 숨겨야 하는 경로들
  const hiddenPathPatterns = [
    ROUTES.LOGIN.pathname, // 로그인 페이지
    ROUTES.AUTH.pathname, // 로그인 인증 페이지
    ROUTES.EASY_CERTIFICATION.pathname, // 인증 페이지
    ROUTES.MY_EDIT.pathname, // 프로필 수정 페이지
    ROUTES.SEARCH.pathname, // 검색 페이지
    ROUTES.COMMUNITY.pathname, // 커뮤니티 전체 페이지
    ROUTES.RECOMMEND_POLICY_LIST.pathname,
    ROUTES.MEDIAN_INCOME.pathname,
    '/join', // 회원가입 페이지
    '/kb-join', // KB 전용 회원가입 페이지
    '/event', // 이벤트 페이지
    '/donation/event',
    DONATION_ROUTES.CART.pathname,
    `${WEL_MARKET_ROUTES.HOME.pathname}/order`, // 웰로켓마켓 결제 페이지
    ROUTES.WELLO_AGENT.pathname, // 웰로 AI 에이전트 페이지
  ];

  // 채널톡 버튼 위치를 위로 올려야 하는 경로들 (하단 CTA와 겹침 방지)
  const elevatedPathPatterns = [
    DONATION_ROUTES.GIFTS.pathname,
    `${WEL_MARKET_ROUTES.HOME.pathname}/products`,
    WEL_MARKET_ROUTES.CART.pathname,
  ];

  const isHiddenByPath =
    hiddenExactPathnames.includes(pathname ?? '') ||
    hiddenPathPatterns.some((pattern) => pathname?.startsWith(pattern));

  const shouldHide = isMessengerVisible || isHiddenByPath;

  const shouldElevate = elevatedPathPatterns.some((pattern) =>
    pathname?.startsWith(pattern),
  );

  const handleChannelButtonClick = () => {
    ChannelService.showMessenger();
  };

  useEffect(() => {
    const pluginKey = ENV.NEXT_PUBLIC_CHANNEL_TALK_PLUGIN_KEY;

    if (!pluginKey) {
      console.warn('Channel Talk plugin key is not configured');

      return;
    }

    const filterUndefined = (obj: Record<string, unknown>): Profile => {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value as
            | string
            | number
            | boolean
            | string[]
            | number[]
            | null;
        }

        return acc;
      }, {} as Profile);
    };

    const initChannelTalk = () => {
      ChannelService.loadScript();
      isChannelLoadedRef.current = true;

      ChannelService.boot(
        {
          pluginKey,
          customLauncherSelector: '#custom-channel-button',
          hideChannelButtonOnBoot: true,
          ...(isLogin && myInfo
            ? {
                memberId: myInfo.memberOAuth?.toString(),
                profile: filterUndefined({
                  ...myInfo,
                }),
              }
            : {}),
        },
        (error) => {
          if (error) {
            console.error('Channel Talk boot error:', error);
          }
        },
      );

      ChannelService.onBadgeChanged((unread, alert) => {
        setUnreadCount(unread);
        setAlertCount(alert);
      });

      ChannelService.onShowMessenger(() => {
        setIsMessengerVisible(true);
        isMessengerVisibleRef.current = true;
      });

      ChannelService.onHideMessenger(() => {
        setIsMessengerVisible(false);
        isMessengerVisibleRef.current = false;
      });

      ChannelService.onUrlClicked(() => {
        if (!isMessengerVisibleRef.current) {
          ChannelService.track('PopupBannerClick');
        }
      });
    };

    //* 브라우저 idle 시점에 로드하여 초기 렌더링 성능 확보
    const idleCallbackId =
      typeof requestIdleCallback !== 'undefined'
        ? requestIdleCallback(initChannelTalk, { timeout: 5000 })
        : undefined;

    const timeoutId =
      idleCallbackId === undefined
        ? setTimeout(initChannelTalk, 3000)
        : undefined;

    return () => {
      if (idleCallbackId !== undefined) cancelIdleCallback(idleCallbackId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (isChannelLoadedRef.current) {
        ChannelService.clearCallbacks();
        ChannelService.shutdown();
        isChannelLoadedRef.current = false;
      }
    };
  }, [isLogin, myInfo]);

  if (shouldHide) {
    return null;
  }

  const chTalkUrl = imageLoader({
    src: `${WELLO_IMAGE_PREFIX_URL}/static/ch-talk.png`,
    width: 120,
    quality: 75,
  });

  return (
    <div
      style={{
        position: 'fixed',
        right: '20px',
        bottom: `max(${shouldElevate ? 150 : 80}px, calc(var(--bottom-nav-portal-height, 0px) + 20px))`,
        zIndex: 10,
      }}
    >
      <button
        type="button"
        id="custom-channel-button"
        onClick={handleChannelButtonClick}
        style={{
          position: 'relative',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: 0,
          backgroundImage: `url(${chTalkUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'scale(0.9)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        aria-label="채널톡 열기"
      />
      {alertCount > 0 ? (
        <div
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            minWidth: '20px',
            height: '20px',
            borderRadius: '10px',
            backgroundColor: '#ff4757',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        >
          {alertCount > 99 ? '99+' : alertCount}
        </div>
      ) : unreadCount > 0 ? (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#ff4757',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        />
      ) : null}
    </div>
  );
};
